import { Grid, Switch, Typography } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import {
  getSamsungSSPTiersEnabledForPublisher,
  getSamsungSSPTiersForPublisher,
  updateSamsungSSPTiersEnabledForPublisher,
  updateSamsungSSPTiersForPublisher,
} from '~/src/dash/api/demand-settings/samsungssp/samsungssp-tiers_methods';
import { ISamsungSSPTier } from '~/src/dash/api/demand-settings/samsungssp/samsungssp-tiers_models';
import { EBool } from '~/src/dash/api/types/bool';
import { SamsungSSPTierTable } from '~/src/dash/components/demand_settings/samsung_ssp_tier_table';
import { IStore } from '~/src/dash/reducers/store';
import {
  getResellerTitle,
  isEvrideo,
  isOrka,
  isOttera,
} from '~/src/dash/resellers/utils/importResellerConfig';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  errorColor,
  growGrid,
  growGridLeft,
  growGridRight,
} from '~/src/dash/utils/material-dashboard-react';
import { SnackbarV2 } from '~/src/dash/views/snackbar-view-v2';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  setSaveFunc: (handleSave: () => void) => {};
}

const _IDemandSettingsSamsungSSP = (props: IProps) => {
  const [tiers, setTiers] = useState<ISamsungSSPTier[]>([]);
  const [tiersEnabled, setTiersEnabled] = useState<EBool>(EBool.EBool_FALSE);

  const snackbarV2 = new SnackbarV2();

  const fillDefaultTiers = (tiers?: ISamsungSSPTier[]) => {
    let _tiers = [] as ISamsungSSPTier[];
    if (tiers === undefined || tiers === null) {
      for (let i = 0; i < 11; i++) {
        const publicaTierNum = i + 1 <= 10 ? i + 1 : 10;
        _tiers.push({ ID: 0, PublicaTier: publicaTierNum, SamsungSSPTier: i });
      }
      setTiers(_tiers);
      return;
    } else {
      _tiers = cloneDeep(tiers);
      for (let i = 0; i < 11; i++) {
        if (!tiers.find((element) => element.SamsungSSPTier === i)) {
          const publicaTierNum = i + 1 <= 10 ? i + 1 : 10;
          _tiers.push({ ID: 0, PublicaTier: publicaTierNum, SamsungSSPTier: i });
        }
      }
      setTiers(_tiers);
      return;
    }
  };

  // == TIERS
  const getSamsungSSPTiers = () => {
    getSamsungSSPTiersForPublisher(props.session.publisher.ID)
      .then((_tiers) => {
        if (_tiers) {
          fillDefaultTiers(_tiers as ISamsungSSPTier[]);
        }
      })
      .catch((err) => {
        console.error(err);
        snackbarV2.displayMessage('Unable to load tiers', true);
      });
  };

  const saveTiers = (_tiers: ISamsungSSPTier[]) => {
    fillDefaultTiers(_tiers);
  };

  // === TIERS ENABLED?
  const getIsSamsungSSPTiersEnabled = () => {
    getSamsungSSPTiersEnabledForPublisher(props.session.publisher.ID)
      .then((_isEnabled) => {
        if (_isEnabled !== undefined) {
          setTiersEnabled(_isEnabled as EBool);
        }
      })
      .catch((err) => {
        console.error(err);
        snackbarV2.displayMessage('Unable to load tiers enable status', true);
      });
  };

  const saveIsTiersEnabled = (value: EBool) => {
    setTiersEnabled(value);
  };

  useEffect(() => {
    fillDefaultTiers(tiers);
    getIsSamsungSSPTiersEnabled();
    getSamsungSSPTiers();
  }, []);

  const handleSave = () => {
    // tiers
    updateSamsungSSPTiersForPublisher(props.session.publisher.ID, tiers)
      .then((_tiers) => {
        if (_tiers) {
          fillDefaultTiers(_tiers as ISamsungSSPTier[]);
          snackbarV2.displayMessage('Tiers mapping saved');
        }
      })
      .catch((err) => {
        console.error(err);
        snackbarV2.displayMessage('Unable to save tiers mapping', true);
      });

    // enabled / disabled
    updateSamsungSSPTiersEnabledForPublisher(props.session.publisher.ID, tiersEnabled)
      .then((newValue) => {
        if (newValue) {
          setTiersEnabled(newValue as EBool);
        }
      })
      .catch((err) => {
        console.error(err);
        snackbarV2.displayMessage('Unable to update tiers enable status', true);
      });
  };

  props.setSaveFunc(handleSave);

  return (
    <div>
      <Grid container direction='row' alignItems='center' style={{ marginBottom: '10px' }}>
        <Grid item>
          <Typography style={{ fontSize: 16, fontWeight: 500 }}>Tiers Mapping Table</Typography>
        </Grid>
        <Grid item style={{ marginLeft: '20px' }}>
          {EBool.toBool(tiersEnabled) ? 'Active' : 'Inactive'}
          <Switch
            name='Enable Samsung SSP Tiers'
            checked={EBool.toBool(tiersEnabled)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              saveIsTiersEnabled(EBool.fromBool(event.target.checked));
            }}
          />
        </Grid>
      </Grid>
      {EBool.toBool(tiersEnabled) && (
        <>
          <p>
            Assign {`${isEvrideo() || isOrka() || isOttera() ? 'an ' : 'a '}`} {getResellerTitle()}{' '}
            tier to the tier passed by demand partner in bid response. Exceptions can be set at the
            bidder level.
          </p>
          <SamsungSSPTierTable
            firstColumnTitle='Samsung SSP Tier'
            secondColumnTitle={`${getResellerTitle()}` + ' Tier'}
            tiers={tiers}
            saveFn={saveTiers}
          />
        </>
      )}
      {snackbarV2.component()}
    </div>
  );
};

export const DemandSettingsSamsungSSP = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
      growGrid,
      growGridLeft,
      growGridRight,
      errorColor: {
        color: errorColor,
      },
      clampTableCell: {
        whiteSpace: 'nowrap',
        width: '1px',
      },
    }),
    router: true,
  },
  _IDemandSettingsSamsungSSP,
);
